import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined"
import LoadingButton from "@mui/lab/LoadingButton"
import Alert from "@mui/material/Alert"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import MenuItem from "@mui/material/MenuItem"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { useEffect, useState } from "react"
import { useSelector } from "react-redux"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import { canUsePowerDialer } from "../../users/accountRole"
import { selectProfile } from "../../users/reducer"

function resultMessage(result) {
  const parts = [
    `${result.added.toLocaleString()} lead${
      result.added === 1 ? "" : "s"
    } added`,
  ]
  if (result.already_queued) {
    parts.push(`${result.already_queued.toLocaleString()} already queued`)
  }
  if (result.skipped_dnc) {
    parts.push(`${result.skipped_dnc.toLocaleString()} DNC skipped`)
  }
  return `${parts.join(
    ", "
  )}. ${result.pending_total.toLocaleString()} waiting for the pod.`
}

/** Queue the list (or selection) on screen for a Pod Dialer team calling for this account. */
export default function AssignToPodButton({
  source,
  filters,
  ordering,
  selectedIds,
  disabled = false,
}) {
  const profile = useSelector(selectProfile)
  const axios = useAuthorizedAxiosClient()
  const accountId = profile?.active_account?.id
  const [pods, setPods] = useState([])
  const [open, setOpen] = useState(false)
  const [podId, setPodId] = useState("")
  const [campaignId, setCampaignId] = useState("")
  const [isAssigning, setIsAssigning] = useState(false)
  const [result, setResult] = useState(null)
  const [error, setError] = useState("")
  const allowed =
    canUsePowerDialer(profile) && Boolean(profile?.features?.pod_dialer)

  useEffect(() => {
    if (!allowed) return undefined
    let cancelled = false
    axios
      .get("/pods/api/account-pods/")
      .then(({ data }) => {
        if (cancelled) return
        setPods(data || [])
        setPodId(data?.[0]?.id ?? "")
      })
      .catch(() => {
        if (!cancelled) setPods([])
      })
    return () => {
      cancelled = true
    }
  }, [allowed, axios, accountId])

  if (!allowed || pods.length === 0) return null

  // Clients usually have one campaign; only ask when there are several.
  const campaigns = pods.find((pod) => pod.id === podId)?.campaigns || []
  const needsCampaign = campaigns.length > 1

  const selectedCount = selectedIds?.length || 0

  function handleOpen() {
    setResult(null)
    setError("")
    setOpen(true)
  }

  function handleAssign() {
    setIsAssigning(true)
    setError("")
    axios
      .post("/pods/api/assign-leads/", {
        pod_id: podId,
        campaign_id: needsCampaign ? campaignId : null,
        source,
        filters: filters || [],
        ordering: ordering || "",
        selected_ids: selectedIds || [],
      })
      .then(({ data }) => setResult(data))
      .catch((err) => {
        const data = err?.response?.data
        setError(
          (data && (data.pod?.[0] || data.detail || data.selected_ids?.[0])) ||
            "Could not assign these leads. Please try again."
        )
      })
      .finally(() => setIsAssigning(false))
  }

  return (
    <>
      <Button
        variant="outlined"
        color="primary"
        startIcon={<GroupsOutlinedIcon />}
        disabled={disabled}
        onClick={handleOpen}
        sx={{ textTransform: "none", fontWeight: 600 }}
      >
        Assign to pod
      </Button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Assign leads to a pod</DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}
        >
          <Typography variant="body2" color="text.secondary">
            {selectedCount
              ? `The ${selectedCount} selected lead${
                  selectedCount === 1 ? "" : "s"
                }`
              : "Every lead in this filtered list"}{" "}
            will be queued for the pod. The pod calls them on its schedule; DNC
            leads and leads without a phone are skipped.
          </Typography>
          {pods.length > 1 ? (
            <TextField
              select
              label="Pod"
              size="small"
              value={podId}
              onChange={(e) => {
                setPodId(e.target.value)
                setCampaignId("")
              }}
            >
              {pods.map((pod) => (
                <MenuItem key={pod.id} value={pod.id}>
                  {pod.name}
                </MenuItem>
              ))}
            </TextField>
          ) : (
            <Typography variant="body2">
              Pod: <strong>{pods[0].name}</strong>
            </Typography>
          )}
          {needsCampaign ? (
            <TextField
              select
              label="Campaign"
              size="small"
              value={campaignId}
              onChange={(e) => setCampaignId(e.target.value)}
            >
              {campaigns.map((campaign) => (
                <MenuItem key={campaign.id} value={campaign.id}>
                  {campaign.name}
                </MenuItem>
              ))}
            </TextField>
          ) : null}
          {result ? (
            <Alert severity="success">{resultMessage(result)}</Alert>
          ) : null}
          {error ? <Alert severity="error">{error}</Alert> : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)} sx={{ textTransform: "none" }}>
            {result ? "Done" : "Cancel"}
          </Button>
          {!result ? (
            <LoadingButton
              variant="contained"
              loading={isAssigning}
              disabled={!podId || (needsCampaign && !campaignId)}
              onClick={handleAssign}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              Assign
            </LoadingButton>
          ) : null}
        </DialogActions>
      </Dialog>
    </>
  )
}
